/* eslint-disable max-lines */
import {makeAutoObservable} from 'mobx'
import {ObjUtil} from 'yougile-util'
import {
  DataObjectId,
  DeadlineStickerValue,
  Entity,
  Hub,
  StickerStateId,
  Tag
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {MessageBox} from '@components/message-box'
import {getAllReportTasks} from '@components/statistics/stats-reports/stats-reports-get-tasks'
import {createUseStore} from '@containers/hooks'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {Sticker} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {
  checkTasks,
  commitAction,
  getTasksWithPermissionsToArchive,
  getTasksWithPermissionsToCheck,
  getTasksWithPermissionsToDelete,
  getTasksWithPermissionsToMove,
  getTextWithoutPermission,
  taskIsCompleted
} from './store-utils'

interface ColumnTasks {
  [columnId: DataObjectId]: DataObjectId[]
}

interface ColumnOrderList {
  [columnId: DataObjectId]: Entity[]
}

export interface SelectedColumnTasks {
  [columnId: DataObjectId]: {
    [taskId: DataObjectId]: number
  }
}

export type ActionType =
  | 'check'
  | 'uncheck'
  | 'move'
  | 'archive'
  | 'unarchive'
  | 'delete'
  | ''

interface State {
  visible: boolean
  tasksIds: Set<DataObjectId>
  columnTasks: ColumnTasks
  noRightsToSomeTasks: boolean
  tasksToAction: Hub[]
  currentAction: ActionType
  deletePopup: boolean
  checkedTasks: DataObjectId[]
  uncheckedTasks: DataObjectId[]
  archivedTasks: DataObjectId[]
  unarchivedTasks: DataObjectId[]
  moveLocationId: DataObjectId | undefined
  applyCustomStickerPopup: boolean
  stickerName: string
  stickerState: StickerStateId
  tagOpen: boolean
  tag: Tag | undefined
  deadlineValue: DeadlineStickerValue | undefined
  assignedUsers: DataObjectId[]
  columnOrderList: ColumnOrderList
  selectedColumnTasks: SelectedColumnTasks
}

export class MultiActionsWithTasksStore {
  state: State = {
    visible: false,
    tasksIds: new Set(),
    columnTasks: {},
    noRightsToSomeTasks: false,
    tasksToAction: [],
    currentAction: '',
    deletePopup: false,
    checkedTasks: [],
    uncheckedTasks: [],
    archivedTasks: [],
    unarchivedTasks: [],
    moveLocationId: undefined,
    applyCustomStickerPopup: false,
    stickerName: '',
    stickerState: '' as StickerStateId,
    tagOpen: false,
    tag: undefined,
    deadlineValue: undefined,
    assignedUsers: [],
    columnOrderList: {},
    selectedColumnTasks: {}
  }

  constructor() {
    makeAutoObservable(this, {}, {autoBind: true})
  }

  visibleOff(): boolean | void {
    if (!this.state.visible) {
      return
    }

    this.state.visible = false
    this.state.tasksIds = new Set()
    this.state.columnTasks = {}
    this.state.noRightsToSomeTasks = false
    this.state.tasksToAction = []
    this.state.currentAction = ''
    this.state.deletePopup = false
    this.state.checkedTasks = []
    this.state.uncheckedTasks = []
    this.state.archivedTasks = []
    this.state.unarchivedTasks = []
    this.state.moveLocationId = undefined
    this.state.applyCustomStickerPopup = false
    this.state.stickerName = ''
    this.state.stickerState = '' as StickerStateId
    this.state.tagOpen = false
    this.state.deadlineValue = undefined
    this.state.assignedUsers = []
    this.state.columnOrderList = {}
    this.state.selectedColumnTasks = {}

    void Tracking.send(TrackingEvent.multiActionsWithTasksExit)
    return true
  }

  visibleOn(): void {
    this.state.visible = true
  }

  setSelectedColumnTasks(): void {
    const data: SelectedColumnTasks = {}
    for (const [columnId, tasks] of ObjUtil.entries(
      this.state.columnOrderList
    )) {
      for (const selectedTaskId of this.state.tasksIds) {
        const taskIndex = tasks.findIndex(task => task.id === selectedTaskId)
        if (taskIndex >= 0) {
          data[columnId] = {
            ...data[columnId],
            [selectedTaskId]: taskIndex + 1
          }
        }
      }
    }

    for (const columnId of ObjUtil.keys(data)) {
      if (data[columnId]) {
        const y = data[columnId]
        const sortable = Object.fromEntries(
          Object.entries(y).sort(([, a], [, b]) => a - b)
        )
        data[columnId] = sortable
      }
    }
    this.state.selectedColumnTasks = data
  }

  setAssignedUsers(users: DataObjectId[]): void {
    this.state.assignedUsers = users

    const box = Sync.getCurrentSandbox()
    for (const id of Array.from(this.state.tasksIds)) {
      const task = box.getHub(id)
      if (!task || !this.state.tag) {
        continue
      }
      if (users.length) {
        Sticker.pin(this.state.tag, task.location, task, users)
      } else {
        Sticker.unpin(this.state.tag, task)
      }
    }
  }

  setDeadlineValue(value: DeadlineStickerValue): void {
    this.state.deadlineValue = value
  }

  setNoRightsToSomeTasks(value: boolean): void {
    this.state.noRightsToSomeTasks = value
  }

  setDeletePopup(value: boolean): void {
    this.state.deletePopup = value
  }

  setTasksId(id: DataObjectId): void {
    this.clearCheck()
    const taskIds = new Set(this.state.tasksIds.values())
    if (taskIds.has(id)) {
      taskIds.delete(id)
    } else {
      taskIds.add(id)
    }
    this.state.tasksIds = taskIds
    this.check(Array.from(taskIds))
  }

  allSelected(columnId: DataObjectId): boolean | undefined {
    const columnTasks = this.state.columnTasks[columnId]
    if (!columnTasks) {
      return false
    }
    const filteredSelectedTasks = columnTasks.filter(x =>
      this.state.tasksIds.has(x)
    )
    if (!filteredSelectedTasks.length) {
      return false
    }
    if (filteredSelectedTasks.length === columnTasks.length) {
      return true
    }
    return undefined
  }

  setAllSelected(columnId: DataObjectId, allSelected: boolean): void {
    this.clearCheck()
    if (allSelected) {
      this.selectAll(columnId)
      void Tracking.send(TrackingEvent.multiActionsWithTasksCheckAllInColumn)
    } else {
      this.unselectAll(columnId)
      void Tracking.send(TrackingEvent.multiActionsWithTasksUnCheckAllInColumn)
    }
    this.check(Array.from(this.state.tasksIds))
  }

  selectAll(columnId: DataObjectId): DataObjectId[] {
    const columnTasks = this.state.columnTasks[columnId]
    const taskIds = new Set(this.state.tasksIds.values())
    const selectTaskIds = columnTasks.filter(x => !taskIds.has(x))
    selectTaskIds.forEach(id => taskIds.add(id))
    this.state.tasksIds = taskIds
    return selectTaskIds
  }

  unselectAll(columnId: DataObjectId): DataObjectId[] {
    const columnTasks = this.state.columnTasks[columnId]
    const taskIds = new Set(this.state.tasksIds.values())
    const unselectTaskIds = columnTasks.filter(x => taskIds.has(x))
    unselectTaskIds.forEach(id => taskIds.delete(id))
    this.state.tasksIds = taskIds
    return unselectTaskIds
  }

  clearCheck(): void {
    this.state.checkedTasks = []
    this.state.uncheckedTasks = []
    this.state.archivedTasks = []
    this.state.unarchivedTasks = []
  }

  setColumnTasks(board: Hub): void {
    const box = Sync.getCurrentSandbox()
    this.state.columnTasks = {}
    const columns = Board.listColumns(board, {
      sorted: true
    })
    for (const column of columns) {
      if (column.data.report) {
        this.state.columnTasks[column.id] = getAllReportTasks(
          box,
          column.data.report,
          undefined,
          board
        ).map(x => x.id)
      } else if (Column.isMirror(column)) {
        for (const mirroredColumn of Column.getMirroredColumns(column)) {
          this.state.columnTasks[mirroredColumn.id] = Column.listTasks(
            mirroredColumn,
            true,
            {board: board}
          ).map(x => x.id)
          this.state.columnOrderList[mirroredColumn.id] =
            Column.getTasksOrder(mirroredColumn).getList()
        }
      } else {
        this.state.columnTasks[column.id] = Column.listTasks(column, true, {
          board: board
        }).map(x => x.id)
        this.state.columnOrderList[column.id] =
          Column.getTasksOrder(column).getList()
      }
    }
  }

  check(tasks: DataObjectId[]): void {
    const data = checkTasks(tasks)
    this.state.checkedTasks = this.state.checkedTasks.concat(data.checkedTasks)
    this.state.uncheckedTasks = this.state.uncheckedTasks.concat(
      data.uncheckedTasks
    )
    this.state.archivedTasks = this.state.archivedTasks.concat(
      data.archivedTasks
    )
    this.state.unarchivedTasks = this.state.unarchivedTasks.concat(
      data.unarchivedTasks
    )
  }

  get archivedTasksSize(): number {
    const box = Sync.getCurrentSandbox()
    const tasks = []
    this.state.tasksIds.forEach(taskId => {
      const task = box.getHub(taskId)
      task && Task.isArchived(task) && tasks.push(task)
    })
    return tasks.length
  }

  checkAll(): void {
    const tasks = getTasksWithPermissionsToCheck(
      Array.from(this.state.tasksIds)
    )
    const uncompleted = tasks.filter(t => !taskIsCompleted(t))
    this.performTasksToAction(tasks, uncompleted, 'check')
    void Tracking.send(TrackingEvent.multiActionsWithTasksCompleteTasks)
  }

  uncheckAll(): void {
    const tasks = getTasksWithPermissionsToCheck(
      Array.from(this.state.tasksIds)
    )
    const completed = tasks.filter(t => taskIsCompleted(t))
    this.performTasksToAction(tasks, completed, 'uncheck')
    void Tracking.send(TrackingEvent.multiActionsWithTasksCompleteTasksCancel)
  }

  archiveAll(): void {
    const tasks = getTasksWithPermissionsToArchive(
      Array.from(this.state.tasksIds)
    )
    const unarchived = tasks.filter(t => !Task.isArchived(t))
    this.performTasksToAction(tasks, unarchived, 'archive')
    void Tracking.send(TrackingEvent.multiActionsWithTasksArchive)
  }

  unarchiveAll(): void {
    const tasks = getTasksWithPermissionsToArchive(
      Array.from(this.state.tasksIds)
    )
    const archived = tasks.filter(t => Task.isArchived(t))
    this.performTasksToAction(tasks, archived, 'unarchive')
    void Tracking.send(TrackingEvent.multiActionsWithTasksUnArchive)
  }

  moveAll(locationId: DataObjectId): void {
    const tasksIds = Array.from(this.state.tasksIds)
    const tasks = getTasksWithPermissionsToMove(tasksIds)
    this.state.moveLocationId = locationId
    this.performTasksToAction(tasks, tasks, 'move')
    void Tracking.send(TrackingEvent.multiActionsWithTasksMove)
  }

  deleteAll(): void {
    const tasks = getTasksWithPermissionsToDelete(
      Array.from(this.state.tasksIds)
    )
    this.performTasksToAction(tasks, tasks, 'delete')
    void Tracking.send(TrackingEvent.multiActionsWithTasksDelete)
  }

  performTasksToAction(
    allSelected: Hub[],
    tasksToAction: Hub[],
    currentAction: ActionType
  ): void {
    this.state.currentAction = currentAction
    this.state.tasksToAction = tasksToAction
    if (this.state.tasksToAction.length === 0) {
      MessageBox.warn(getTextWithoutPermission(this.state.currentAction))
      return
    } else if (allSelected.length !== this.state.tasksIds.size) {
      this.state.noRightsToSomeTasks = true
      return
    } else if (currentAction === 'delete') {
      this.state.deletePopup = true
      return
    } else {
      this.state.noRightsToSomeTasks = false
      this.performAction()
    }
  }

  performAction(): void {
    commitAction(this.state.tasksToAction, this.state.currentAction, {
      locationId: this.state.moveLocationId
    })
    this.visibleOff()
  }

  stickerTurnOff(): void {
    if (!this.state.tag) {
      return
    }
    const box = Sync.getCurrentSandbox()
    const type = Sticker.getType(this.state.tag)
    if (type !== 'deadline' && type !== 'user') {
      for (const id of Array.from(this.state.tasksIds)) {
        const task = box.getHub(id)
        if (!task || !this.state.tag) {
          continue
        }
        const value = this.state.stickerState
        if (value) {
          Sticker.pin(this.state.tag, task.location, task, value)
        } else {
          Sticker.unpin(this.state.tag, task)
        }
      }
    }
    this.visibleOff()
  }

  setApplyCustomStickerPopup(value: boolean): void {
    this.state.applyCustomStickerPopup = value
  }

  setStickerName(value: string): void {
    this.state.stickerName = value
  }

  setStickerState(value: StickerStateId): void {
    this.state.stickerState = value
  }

  setTagOpen(value: boolean): void {
    this.state.tagOpen = value
  }

  setTag(tag: Tag): void {
    this.state.tag = tag
  }
}

const multiActionsWithTasksStore = new MultiActionsWithTasksStore()

const [MultiActionsWithTasksContext, useMultiActionsWithTasksStore] =
  createUseStore(MultiActionsWithTasksStore)

export {
  MultiActionsWithTasksContext,
  useMultiActionsWithTasksStore,
  multiActionsWithTasksStore
}
